import { useState, useEffect, useMemo } from 'react';
import client from '../../api/client';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import UpgradeModal from '../upgrade/UpgradeModal';
import AlertCard from './AlertCard';

const SPORT_FILTERS = [
  { key: null, label: 'All' }, { key: 'americanfootball_nfl', label: 'NFL' }, { key: 'americanfootball_ncaaf', label: 'NCAAF' },
  { key: 'basketball_nba', label: 'NBA' }, { key: 'basketball_ncaab', label: 'NCAAB' }, { key: 'baseball_mlb', label: 'MLB' },
  { key: 'icehockey_nhl', label: 'NHL' }, { key: 'soccer_usa_mls', label: 'MLS' },
  { key: 'mma_mixed_martial_arts', label: 'MMA' }, { key: 'boxing_boxing', label: 'Boxing' }, { key: 'golf', label: 'Golf' },
];
const MARKET_FILTERS = [
  { key: null, label: 'All', color: null },
  { key: 'spreads', label: 'Spread', color: '#005BB3' },
  { key: 'h2h', label: 'ML', color: '#029456' },
  { key: 'totals', label: 'Total', color: '#F86A38' },
  { key: 'steam', label: 'Steam', color: '#DC2626' },
  { key: 'rlm', label: 'RLM', color: '#4B5563' },
];
const TIME_FILTERS = [
  { key: 2, label: '2h' }, { key: 6, label: '6h' }, { key: 12, label: '12h' }, { key: 24, label: '24h' },
];

const PAGE_SIZE = 20;

function getMagnitude(alert) {
  const d = alert.discrepancy_data || {};
  if (d.difference) return d.difference;
  if (d.difference_pct) return d.difference_pct;
  if (d.magnitude) return d.magnitude;
  if (d.count) return d.count;
  return 0;
}

const FREE_MAX_HOURS = 6;

export default function ValueAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sportFilter, setSportFilter] = useState(null);
  const [marketFilter, setMarketFilter] = useState(null);
  const [hoursFilter, setHoursFilter] = useState(6);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const tier = useEffectiveTier();
  const [modalOpen, setModalOpen] = useState(false);
  const isFree = tier === 'free';
  const effectiveHours = isFree ? Math.min(hoursFilter, FREE_MAX_HOURS) : hoursFilter;

  function fetchAlerts() {
    setLoading(true); setError(null);
    const params = new URLSearchParams({ hours: String(effectiveHours) });
    if (sportFilter) params.set('sport', sportFilter);
    if (marketFilter) params.set('market', marketFilter);
    client.get(`/api/value-alerts?${params.toString()}`)
      .then((r) => setAlerts(r.data))
      .catch((e) => { setAlerts([]); setError(e.message); })
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchAlerts(); setVisibleCount(PAGE_SIZE); }, [sportFilter, marketFilter, effectiveHours]);

  const sorted = useMemo(() =>
    [...alerts].sort((a, b) => getMagnitude(b) - getMagnitude(a)),
    [alerts],
  );

  const displayed = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < sorted.length;

  return (
    <div className="px-5 py-6">
      <div className="mb-4">
        <h2 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>Value Alerts</h2>
      </div>

      {isFree && (
        <div className="rounded-md border border-[#e2e8f0] dark:border-[#1e2536] bg-[#f8fafc] dark:bg-[#0f1219] px-3 py-2 mb-4 flex items-center justify-between flex-wrap gap-2">
          <p className="text-[13px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>
            Showing alerts from the last 6 hours.{' '}
            <button onClick={() => setModalOpen(true)} className="text-[#005BB3] dark:text-[#3b82f6] font-medium hover:underline">Upgrade to Pro</button>
            {' '}for full alert history.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-4 mb-4 border-b border-[#E5E7EB] dark:border-[#1e2536] pb-0">
        <FilterGroup label="Sport" options={SPORT_FILTERS} value={sportFilter} onChange={setSportFilter} />
        <div className="w-px h-5 bg-[#E5E7EB] dark:bg-[#1e2536] mb-2 flex-shrink-0" />
        <FilterGroup label="Type" options={MARKET_FILTERS} value={marketFilter} onChange={setMarketFilter} useOptionColor />
        <div className="w-px h-5 bg-[#E5E7EB] dark:bg-[#1e2536] mb-2 flex-shrink-0" />
        <FilterGroup label="Time" options={isFree ? TIME_FILTERS.filter((t) => t.key <= FREE_MAX_HOURS) : TIME_FILTERS} value={hoursFilter} onChange={setHoursFilter} />
      </div>

      <UpgradeModal open={modalOpen} onClose={() => setModalOpen(false)} suggestedTier="pro" triggerFeature="alerts_history" />

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-3">
          <p className="text-[#DC2626] text-[12px]">Failed to load: {error}</p>
          <button onClick={fetchAlerts} className="text-[11px] text-[#DC2626] underline mt-1">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}</div>
      ) : sorted.length === 0 && !error ? (
        <div className="text-center py-12">
          <p className="text-[#6B7280] dark:text-[#8892a8] text-[14px]" style={{ fontFamily: 'var(--font-sans)' }}>No discrepancies detected.</p>
          <p className="text-[#9CA3AF] dark:text-[#505a6e] text-[13px] mt-1" style={{ fontFamily: 'var(--font-sans)' }}>Alerts appear when bookmaker odds diverge from consensus.</p>
        </div>
      ) : (
        <>
          <div className="max-w-[1100px]">
            <p className="text-[13px] text-[#6B7280] mb-3" style={{ fontFamily: 'var(--font-sans)' }}>
              {sorted.length} alert{sorted.length !== 1 ? 's' : ''} in last {hoursFilter}h, sorted by significance
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {displayed.map((a, i) => <AlertCard key={a.id || i} alert={a} />)}
            </div>
            {hasMore && (
              <button
                onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                className="w-full mt-3 py-2 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] text-[13px] text-[#005BB3] dark:text-[#3b82f6] hover:bg-[#F8F9FA] dark:hover:bg-[#151a25] transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}
              >
                Show more ({sorted.length - visibleCount} remaining)
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FilterGroup({ label, options, value, onChange, useOptionColor }) {
  return (
    <div className="flex items-end gap-0">
      <span className="text-[11px] uppercase tracking-[1px] font-semibold text-[#9CA3AF] dark:text-[#505a6e] mr-3 pb-2 select-none" style={{ fontFamily: 'var(--font-sans)' }}>{label}</span>
      {options.map((o) => {
        const isActive = value === o.key;
        const activeColor = useOptionColor && o.color ? o.color : '#005BB3';
        return (
          <button key={o.label} onClick={() => onChange(o.key)}
            className={`px-0 mr-4 pb-2 text-[13px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
              isActive
                ? 'font-semibold text-[#111827] dark:text-[#e2e8f0]'
                : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
            }`}
            style={{ fontFamily: 'var(--font-sans)', borderBottomColor: isActive ? activeColor : 'transparent' }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
